import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { OrderProvider } from './contexts/OrderContext';
import { SessionProvider } from './contexts/SessionContext';
import { DebugModeProvider } from './contexts/DebugModeContext';
import { PartSelectionModeProvider } from './contexts/PartSelectionModeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import HomePage from './pages/HomePage';
import DiagramHarnessPage from "./pages/dev/DiagramHarnessPage";
import ChatPage from './pages/ChatPage';
import LoginPage from './pages/LoginPage';
import SessionsPage from './pages/SessionsPage';
import SettingsPage from './pages/SettingsPage';
import OrdersManagement from './pages/OrdersManagement';
import TeamPage from './pages/TeamPage';
import OrganizationPage from './pages/OrganizationPage';
import AdminPage from './pages/AdminPage';
import AdminSessionDetailsPage from './pages/AdminSessionDetailsPage';
import SalesChannelsPage from './pages/SalesChannelsPage';
import GenericIntegrationSetupPage from './pages/GenericIntegrationSetupPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookieSettings from './pages/CookieSettings';
import CookiesAlert from './components/common/CookiesAlert';
import WaitingForApprovalPage from './pages/WaitingForApprovalPage';
import ROIDashboard from './pages/ROIDashboard';
import UsagePlanPage from './pages/UsagePlanPage';
import UsageMemberPage from './pages/UsageMemberPage';
import { QuotaProvider } from './contexts/QuotaContext';
import ROICalculatorPage from './pages/ROICalculatorPage';
import ROIB2CCalculatorPage from './pages/ROIB2CCalculatorPage';
import GuestChatPage from './pages/GuestChatPage';
import OAuthConsentPage from './pages/OAuthConsentPage';
import WorkflowsPage from './pages/WorkflowsPage';
import WorkflowReadOnlyPage from './pages/WorkflowReadOnlyPage';
import DiscountDashboard from './pages/DiscountDashboard';
import PortalRouter from './pages/portal/PortalRouter';
import ProtectedRoute from './components/common/ProtectedRoute';
import OrgAdminOnly from './components/common/OrgAdminOnly';
import UpdateBanner from './components/common/UpdateBanner';
import OtherOrgWarning from './components/common/OtherOrgWarning';
import OrgFromLink from './components/common/OrgFromLink';
import { useVersionCheck } from './hooks/useVersionCheck';
import './i18n/config'; // Initialize i18n
import './App.css';

const DevelopmentErrorTrigger: React.FC = () => {
  const shouldCrash = process.env.NODE_ENV === 'development'
    && new URLSearchParams(window.location.search).get('testErrorBoundary') === '1';

  if (shouldCrash) {
    throw new Error('Intentional development crash for testing the Cata support error report');
  }
  return null;
};

const ROIProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser?.roi_calculator_enabled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Smart redirect: sends user to whichever calculator type they have access to.
// B2B-only → /roi-calculator, B2C-only → /roi-calculator/b2c, both → /roi-calculator (B2B default).
const ROIRedirectRoute: React.FC = () => {
  const { currentUser } = useAuth();
  if (!currentUser?.roi_calculator_enabled) return <Navigate to="/" replace />;
  if (!currentUser?.roi_b2b_enabled && currentUser?.roi_b2c_enabled) {
    return <Navigate to="/roi-calculator/b2c" replace />;
  }
  if (currentUser?.roi_b2b_enabled) {
    return <Navigate to="/roi-calculator/b2b" replace />;
  }
  return <Navigate to="/" replace />;
};

const ROIB2BProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser?.roi_calculator_enabled || !currentUser?.roi_b2b_enabled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const ROIB2CProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser?.roi_calculator_enabled || !currentUser?.roi_b2c_enabled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/** Shows the read-only workflow view for all users. */
const WorkflowRoute: React.FC = () => {
  return <WorkflowReadOnlyPage />;
};

/**
 * Syncs the UI language with the organization-level language setting
 * whenever the authenticated user changes. The effective language is
 * returned directly from the /api/user/login response, so no extra
 * round-trip is needed. Falls back to syncWithOrganization() if the
 * language is not present on the user object (e.g. page refresh).
 */
const OrgLanguageSync: React.FC = () => {
  const { currentUser } = useAuth();
  const { changeLanguage, syncWithOrganization } = useLanguage();

  useEffect(() => {
    if (!currentUser) return;

    if (currentUser.language) {
      // Language came directly from the login response — apply immediately
      const supported = ['en', 'es', 'es-MX', 'es-CL', 'es-EC', 'pt'];
      if (supported.includes(currentUser.language)) {
        changeLanguage(currentUser.language as any);
        return;
      }
    }

    // Fallback: fetch from settings endpoint (e.g. on page refresh where
    // onAuthStateChanged fires but language may not be on the cached user)
    syncWithOrganization();
    // Depend on language too (not just uid): the auth flow first sets a basic
    // user (uid only) and only later swaps in the full backend profile carrying
    // `language`. With uid-only deps that second update never re-ran this
    // effect, so if the settings-endpoint fallback failed the UI silently
    // stayed on the browser/localStorage language while the backend kept
    // answering in the org language — mixed-language chat chrome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.language]);

  return null;
};

function App() {
  const { updateAvailable, reload } = useVersionCheck();

  return (
    <ErrorBoundary>
      <DevelopmentErrorTrigger />
      {updateAvailable && (
        <UpdateBanner onReload={reload} />
      )}
      <LanguageProvider>
        <AuthProvider>
          <OrgLanguageSync />
          <OtherOrgWarning />
          <OrgFromLink />
          <OrderProvider>
            <SessionProvider>
              <DebugModeProvider>
              <PartSelectionModeProvider>
              <QuotaProvider>
              <Router>
                <div className="App">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    {(process.env.NODE_ENV === "development" || process.env.REACT_APP_DEV_HARNESS === "1") && <Route path="/dev/diagram-harness" element={<DiagramHarnessPage />} />}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/waiting-approval" element={<WaitingForApprovalPage />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/cookie-settings" element={<CookieSettings />} />
                    {/* Public guest chat — no login required */}
                    <Route path="/guest/:token" element={<GuestChatPage />} />
                    {/* OAuth 2.0 consent screen for MCP clients. Public route —
                        the page itself sends unauthenticated users through login. */}
                    <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                    <Route
                      path="/chat/:sessionId"
                      element={
                        <ProtectedRoute>
                          <ChatPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/chat"
                      element={
                        <ProtectedRoute>
                          <ChatPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sessions"
                      element={
                        <ProtectedRoute>
                          <SessionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <SettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/orders"
                      element={
                        <ProtectedRoute>
                          <OrdersManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <ROIDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usage/members/:memberKey"
                      element={
                        <ProtectedRoute>
                          <OrgAdminOnly>
                            <UsageMemberPage />
                          </OrgAdminOnly>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usage"
                      element={
                        <ProtectedRoute>
                          <OrgAdminOnly>
                            <UsagePlanPage />
                          </OrgAdminOnly>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/team"
                      element={
                        <ProtectedRoute>
                          <TeamPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/organization"
                      element={
                        <ProtectedRoute>
                          <OrganizationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/roi-calculator"
                      element={
                        <ProtectedRoute>
                          <ROIRedirectRoute />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/roi-calculator/b2b"
                      element={
                        <ProtectedRoute>
                          <ROIB2BProtectedRoute>
                            <ROICalculatorPage />
                          </ROIB2BProtectedRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/roi-calculator/b2c"
                      element={
                        <ProtectedRoute>
                          <ROIB2CProtectedRoute>
                            <ROIB2CCalculatorPage />
                          </ROIB2CProtectedRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/channels"
                      element={
                        <ProtectedRoute>
                          <SalesChannelsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/workflows"
                      element={
                        <ProtectedRoute>
                          <WorkflowRoute />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/discounts"
                      element={
                        <ProtectedRoute>
                          <DiscountDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/integration-setup/:integrationId"
                      element={
                        <ProtectedRoute>
                          <GenericIntegrationSetupPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* B2B Customer self-service portal — no Firebase auth needed */}
                    <Route path="/portal/:orgAlias/*" element={<PortalRouter />} />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/sessions/:userId/:sessionId"
                      element={
                        <ProtectedRoute>
                          <AdminSessionDetailsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/:tab"
                      element={
                        <ProtectedRoute>
                          <AdminPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                  <CookiesAlert />
                </div>
              </Router>
              </QuotaProvider>
              </PartSelectionModeProvider>
              </DebugModeProvider>
            </SessionProvider>
          </OrderProvider>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
